import React from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  hasPlatformPermission,
  type PlatformPermissionKey,
} from "@/utils/accessControl";
import { PageState } from "@/components/common/PageState";

interface PlatformPermissionGuardProps {
  permission: PlatformPermissionKey;
  children: React.ReactNode;
  fallbackPath?: string;
}

export const PlatformPermissionGuard: React.FC<
  PlatformPermissionGuardProps
> = ({ permission, children, fallbackPath }) => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (!hasPlatformPermission(userProfile, permission)) {
    if (fallbackPath) {
      return <Navigate replace to={fallbackPath} />;
    }

    return (
      <PageState
        tone="error"
        text={t(
          "common.insufficientPlatformPermissions",
          "Insufficient platform permissions.",
        )}
      />
    );
  }

  return <>{children}</>;
};
